import { type FC, memo, useMemo } from 'react'
import dynamic from 'next/dynamic'
import styled from 'styled-components'
import { MODALS, TypePageDesignContext } from '@/context'
import { useModal, usePageDesign, useScrollTracker, useTranslator } from '@/hooks'
import { getIcon } from '@/utils'
import type { TypeCmsLayout, TypeDevice } from '@/schema'
import { createUnderline, designGrid, getP18, getTransition, mq, vw } from '@/styles'
import { Link } from '../Link'
import { Img } from '../Img'
import { ModalMenu } from '@/components/modals/ModalMenu'
import { Logo } from './Logo'
import { motion, Variants } from 'motion/react'

const DynamicModalContact = dynamic(() => import('@/components/modals/ModalContact').then(mod => mod.ModalContact), { ssr: false })
const DynamicModalNewsletter = dynamic(() => import('@/components/modals/ModalNewsletter').then(mod => mod.ModalNewsletter), { ssr: false })
const DynamicModalSearch = dynamic(() => import('@/components/modals/ModalSearch').then(mod => mod.ModalSearch), { ssr: false })

type Props = {
  headerAgentDeviceType?: TypeDevice
  data: TypeCmsLayout['navbar']
}

type NavProps = {
  $menuIsActive: boolean
  $scrollTop: boolean
  $pageHasHeaderBgImage: TypePageDesignContext['pageHasHeaderBgImage']
  $textColor?: string
}

export const NAVBAR_HEIGHTS = {
  mobile: 50,
  desktop: 80,
}

export const Navbar:FC<Props> = memo(({ data }) => {
  const { t } = useTranslator()
  const { isActive:menuIsActive, toggle } = useModal(MODALS.MENU)
  const { toggle:toggleSearch } = useModal(MODALS.SEARCH)
  const { pageHasHeaderBgImage, headerTextColor } = usePageDesign()
  const { scrollOnTop, scrollState } = useScrollTracker(50)
  const varTextColor = useMemo(() => `var(--color-${(!headerTextColor || headerTextColor === 'dark') ? 'text' : (!scrollOnTop || menuIsActive || !pageHasHeaderBgImage) ? 'text' : 'white'})`, [headerTextColor, scrollOnTop, menuIsActive, pageHasHeaderBgImage])

  return (
    <Nav variants={NAV_VARIANTS} initial='initial' animate={(scrollState === 'SCROLLING_UP' || scrollState === 'TOP') ? 'animate' : 'exit'} exit='exit' $menuIsActive={menuIsActive} $scrollTop={scrollOnTop} $pageHasHeaderBgImage={pageHasHeaderBgImage} $textColor={varTextColor}>
      <button id='menu-toggle' className='btn--menu' aria-expanded={menuIsActive} aria-controls='nav-links' onClick={toggle}>
        <span className='srt-only'>Open menu</span>
        {!menuIsActive ? <SVG varTextColor={varTextColor}/> : <Img media={getIcon('close')}/>}
      </button>
      <Logo color={varTextColor} />
      <dl className='list--info'>
        <dt className='srt-only'>Info</dt>
        <dd><Link href={'/wheretobuy'}>{t('project.navbar.where_to_buy')}</Link></dd>
        {/* <dd><Link href={'/usa/products/all'}>{t('project.navbar.usa_products')}</Link></dd> */}
        <dd><Link href={'/contact'}>{t('project.contact_section.contact_us')}</Link></dd>
      </dl>
      <button className='btn--search-trigger' aria-label={t('actions.search') as string} onClick={toggleSearch}>
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
          <g transform="translate(11.2 10.4)" stroke={varTextColor} strokeWidth=".5" fill="none">
            <circle cx="6.84" cy="6.84" r="6.84" />
            <path d="M11.32 12.37L18.95 20" />
          </g>
        </svg>
      </button>
      <DynamicModalContact />
      <DynamicModalNewsletter />
      <DynamicModalSearch />
      <ModalMenu navbarHeights={NAVBAR_HEIGHTS} data={data}/>
    </Nav>
  )
})

const Nav = styled(motion.nav)<NavProps>`
  ${designGrid({})}
  background-color: ${({ $menuIsActive, $scrollTop, $pageHasHeaderBgImage }) => (!$scrollTop || $menuIsActive || !$pageHasHeaderBgImage) ? 'var(--color-background)' : 'transparent'};
  color: ${({ $textColor }) => $textColor};
  height: ${vw(NAVBAR_HEIGHTS.mobile, 'mobile')};
  min-height: ${NAVBAR_HEIGHTS.mobile}px;
	overflow-x: clip;
  position: absolute;
  top: 0;
  transition: 250ms background-color ease-in-out;
  width: 100dvw;
  width: 100vw;
  z-index: 5001;

  ${mq.greaterThan('nexus7')} {
    height: ${vw(NAVBAR_HEIGHTS.mobile, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    height: ${vw(NAVBAR_HEIGHTS.desktop, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    height: ${NAVBAR_HEIGHTS.desktop}px;
  }

  .btn--menu {
    align-items: center;
    grid-column: 1 / span 2;
    grid-row: 1 / span 1;
    height: 100%;
    justify-content: center;
    padding: 0 5px;
    transform: translate(-15px);
    width: 50px;

    svg {
      ${mq.greaterThan('tablet')} {
        width: ${vw(50, 'desktop')};
        height: ${vw(50, 'desktop')};
      }
    }
  }

  .list--info {
    display: none;

    ${mq.greaterThan('tablet')} {
      align-items: center;
      display: flex;
      gap: ${vw(20, 'desktop')};
      grid-column: 7 / span 5;
      grid-row: 1 / span 1;
      justify-content: flex-end;
    }

    ${mq.greaterThan('desktop')} {
      gap: 20px;
    }

    button,
    a {
      display: inline-block;
      ${getP18()}
      letter-spacing: -.1px;
      ${({ $textColor }) => createUnderline({ reverse: true, color: $textColor })}
    }
  }

  .btn--search-trigger {
    grid-column: 6 / span 1;
    grid-row: 1 / span 1;
    height: 100%;
    justify-content: center;
    margin-left: auto;
    padding: 0 5px;
    transform: translate(10px);
    width: 50px;

    ${mq.greaterThan('tablet')} {
      grid-column: 12 / span 1;
    }
  }
`



const variants = {
  'initial': {
    opacity: 0,
    transition: {
      ...getTransition(),
      duration: .1,
    }
  },
  'animate': {
    opacity: 1,
    transition: {
      ...getTransition(),
      duration: .1,
    }
  },
  'exit': {
    opacity: 0,
    transition: {
      ...getTransition(),
      duration: .1,
    }
  },
}

const variants_props = {
  'initial': 'initial',
  'animate': 'animate',
  'exit': 'exit',
}

const SVG = ({varTextColor}) => (
  <motion.svg key='inacitve' variants={variants} {...variants_props} width="50px" height="50px" viewBox="0 0 50 50" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
    <g id="Symbols" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <g id="i--menu" transform="translate(-32.000000, -21.000000)">
        <g transform="translate(32.000000, 21.000000)">
          <rect id="Rectangle" x="0" y="0" width="50" height="50"></rect>
          <line className='burger-line' x1="8.5" y1="19.5" x2="40.5" y2="19.5" id="Line" stroke={varTextColor} strokeWidth="0.5" strokeLinecap="square"></line>
          <line className='burger-line' x1="8.5" y1="30.5" x2="40.5" y2="30.5" id="Line-Copy" stroke={varTextColor} strokeWidth="0.5" strokeLinecap="square"></line>
        </g>
      </g>
    </g>
  </motion.svg>
)


const NAV_VARIANTS:Variants = {
	initial: {
		y: '-100%',
		transitionEnd: {
			position: 'absolute',
		}
	},
	animate: {
		y: 0,
		position: 'fixed',
	},
	exit: {
		y: '-100%',
		transitionEnd: {
			position: 'absolute',
		}
	}
}